import { useMemo, useState } from "react";
import { Link } from "wouter";
import { Shell } from "@/components/layout/Shell";
import { useSeo } from "@/hooks/use-seo";
import { ShareButtons } from "@/components/social/ShareButtons";
import { MOOD_PALETTE, getMoodOption } from "@/data/moods";
import { getTodayMoodId, setTodayMood, getStreak, getRecentHistory } from "@/lib/mood-storage";
import { classifyAuraFamily, AURA_READS } from "@/lib/aura-extract";

const HISTORY_DAYS = 35; // 5 full weeks

export default function Pulse() {
  useSeo({
    title: "Daily Color Pulse — Mood Tracker | Mychromamind",
    description:
      "Pick the color that matches your mood today and watch your Mood Calendar build over time — a fast, private, color-psychology mood tracker with no account needed.",
    path: "/pulse",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Daily Color Pulse",
      applicationCategory: "LifestyleApplication",
      operatingSystem: "Any (Web)",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      description:
        "A daily mood tracker that lets you log how you feel as a color, building a private, on-device Mood Calendar over time.",
      url: "https://mychromamind.com/pulse",
    },
  });

  const [todayMoodId, setTodayMoodIdState] = useState<string | null>(() => getTodayMoodId());
  // "Confirmed" = show the today's-pick summary instead of the picker grid.
  // Starts true if a mood was already logged today (e.g. returning visitor).
  const [confirmed, setConfirmed] = useState(() => getTodayMoodId() !== null);

  const history = useMemo(() => getRecentHistory(HISTORY_DAYS), [todayMoodId]);
  const streak = useMemo(() => getStreak(), [todayMoodId]);
  const todayMood = todayMoodId ? getMoodOption(todayMoodId) : null;

  const handlePick = (moodId: string) => {
    setTodayMood(moodId);
    setTodayMoodIdState(moodId);
    setConfirmed(true);
  };

  return (
    <Shell>
      <div className="flex-1 px-5 sm:px-6 py-8 sm:py-14 max-w-3xl mx-auto w-full">
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted text-foreground text-xs font-medium tracking-wide uppercase mb-4">
            <span className="w-2 h-2 rounded-full bg-primary" />
            Daily Color Pulse
          </div>
          <h1 className="text-3xl sm:text-5xl font-serif text-foreground leading-[1.1] mb-3">
            What color is your mood today?
          </h1>
          <p className="text-sm sm:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Pick one color, once a day. No account, no sign-up — your history is saved privately on this
            device and builds into a Mood Calendar below.
          </p>
        </div>

        {todayMood ? (
          <div className="text-center mb-10">
            <div
              className="w-16 h-16 rounded-full mx-auto ring-4 ring-border shadow-sm mb-4"
              style={{ backgroundColor: todayMood.hex }}
              aria-hidden="true"
            />
            <p className="text-sm text-muted-foreground mb-1">Today you're feeling</p>
            <p className="text-2xl font-serif text-foreground mb-4">{todayMood.label}</p>
            {streak > 1 && (
              <p className="text-xs text-primary font-medium mb-4">🔥 {streak}-day streak</p>
            )}
            <button
              type="button"
              onClick={() => setConfirmed(false)}
              className="text-xs text-muted-foreground hover:text-primary underline underline-offset-4 transition-colors"
            >
              Change today's pick
            </button>
          </div>
        ) : null}

        {!confirmed && <MoodGrid onPick={handlePick} selectedId={todayMoodId} />}

        {todayMood && confirmed && (() => {
          const auraFamily = classifyAuraFamily(todayMood.hex);
          const aura = AURA_READS[auraFamily];
          const excerpt = aura.description.split(". ").slice(0, 2).join(". ") + ".";
          return (
            <>
              <div className="max-w-sm mx-auto mb-8 rounded-2xl border border-border bg-card/60 p-5 text-left">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-1">
                  Today's Aura Signature
                </p>
                <p className="text-lg font-serif mb-2">{aura.title}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{excerpt}</p>
                <Link
                  href="/aura-scanner"
                  className="mt-3 inline-block text-xs text-primary hover:underline underline-offset-4"
                >
                  Scan a photo for your full aura read →
                </Link>
              </div>
              <ShareButtons
                label="Share your mood color"
                text={`My color mood today is ${todayMood.label} (${todayMood.colorName}) — check yours on Mychromamind.`}
                className="mb-10"
              />
              <p className="text-center text-sm mb-10">
                <Link
                  href={`/learn/${todayMood.id}`}
                  className="text-primary hover:underline underline-offset-4"
                >
                  See what {todayMood.colorName} means →
                </Link>
              </p>
            </>
          );
        })()}

        <div className="mt-4 pt-8 border-t border-border">
          <h2 className="text-lg font-serif mb-1 text-center">Your Mood Calendar</h2>
          <p className="text-xs text-muted-foreground text-center mb-6">Last {HISTORY_DAYS} days</p>
          <MoodCalendar history={history} />
        </div>

        <p className="text-xs text-muted-foreground text-center mt-10 max-w-md mx-auto">
          For entertainment and self-reflection only — not psychological or medical advice. Your mood
          history stays in this browser and is never sent anywhere.
        </p>
      </div>
    </Shell>
  );
}

function MoodGrid({
  onPick,
  selectedId,
}: {
  onPick: (id: string) => void;
  selectedId?: string | null;
}) {
  return (
    <div
      role="group"
      aria-label="Pick today's mood color"
      className="grid grid-cols-4 sm:grid-cols-8 gap-3 sm:gap-4 mb-10"
    >
      {MOOD_PALETTE.map((mood) => (
        <button
          key={mood.id}
          type="button"
          onClick={() => onPick(mood.id)}
          aria-pressed={selectedId === mood.id}
          className="group flex flex-col items-center gap-2 focus:outline-none"
        >
          <span
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full ring-2 ring-border group-hover:ring-primary/60 group-focus-visible:ring-primary transition-all group-hover:scale-105"
            style={{ backgroundColor: mood.hex }}
            aria-hidden="true"
          />
          <span className="text-[10px] sm:text-xs text-muted-foreground group-hover:text-primary transition-colors text-center leading-tight">
            {mood.label}
          </span>
        </button>
      ))}
    </div>
  );
}

function MoodCalendar({ history }: { history: { date: Date; moodId: string | null }[] }) {
  const todayStr = new Date().toDateString();
  return (
    <div className="grid grid-cols-7 gap-1.5 sm:gap-2 max-w-md mx-auto" role="img" aria-label="Mood calendar for the last 35 days">
      {history.map(({ date, moodId }) => {
        const mood = moodId ? getMoodOption(moodId) : null;
        const isToday = date.toDateString() === todayStr;
        return (
          <div
            key={date.toISOString()}
            title={`${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}${
              mood ? ` — ${mood.label}` : ""
            }`}
            className={`aspect-square rounded-md border ${
              isToday ? "border-primary ring-1 ring-primary" : "border-border/60"
            }`}
            style={{ backgroundColor: mood ? mood.hex : "transparent" }}
          />
        );
      })}
    </div>
  );
}
